import { type Dispatch, type SetStateAction, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import RequestStatus from 'components/RequestStatus'
import type {
  FieldSection,
  FieldTranslation,
  FieldType,
  LocalField,
  LocalOption,
  MarketLanguage,
  OptionTranslation,
} from './FormBuilder.types'
import { TrashIcon } from 'assets/icons/trash'
import Add from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DownloadIcon from '@mui/icons-material/Download'

// ── Constants ─────────────────────────────────────────────────────────────────

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  single_select: 'Single select',
  dropdown: 'Dropdown',
  multi_select: 'Multi select',
  file: 'File upload',
  file_text: 'File + description',
}

const HAS_OPTIONS: FieldType[] = ['single_select', 'dropdown', 'multi_select']

// Powyżej tej liczby opcji lista robi się ciężka do renderu (np. 1000 pozycji),
// więc pokazujemy tylko wycinek i wymuszamy filtrowanie.
const OPTIONS_RENDER_CAP = 100

const FIELD_SECTION_LABELS: Record<FieldSection, string> = {
  contest: 'Contest',
  address: 'Address',
  info: 'Info',
  custom: 'Custom',
}

function makeEmptyOptionTranslations(languages: MarketLanguage[]): OptionTranslation[] {
  return languages.map(lang => ({ marketLanguageId: lang.id, label: '' }))
}

// ── FieldCard ────────────────────────────────────────────────────────────────

function FieldCard({
  field,
  index,
  total,
  marketId,
  languages,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  field: LocalField
  index: number
  total: number
  marketId?: string
  languages: MarketLanguage[]
  onChange: (updated: LocalField) => void
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}) {
  const [expanded, setExpanded] = useState(!field.id) // auto-expand new fields
  const [langTab, setLangTab] = useState(0)
  const [newOptionValue, setNewOptionValue] = useState('')
  const [optionFilter, setOptionFilter] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const update = (patch: Partial<LocalField>) => onChange({ ...field, ...patch })

  const setTranslation = (langIdx: number, patch: Partial<FieldTranslation>) => {
    const translations = field.translations.map((t, i) => (i === langIdx ? { ...t, ...patch } : t))
    update({ translations })
  }

  const addOption = () => {
    if (!newOptionValue.trim()) return
    const option: LocalOption = {
      value: newOptionValue.trim(),
      sortOrder: field.options.length,
      isCorrect: false,
      translations: makeEmptyOptionTranslations(languages),
    }
    update({ options: [...field.options, option] })
    setNewOptionValue('')
  }

  const updateOption = (optIdx: number, patch: Partial<LocalOption>) => {
    const options = field.options.map((o, i) => (i === optIdx ? { ...o, ...patch } : o))
    update({ options })
  }

  const setOptionTranslation = (optIdx: number, langId: number, label: string) => {
    const options = field.options.map((o, i) => {
      if (i !== optIdx) return o
      const translations = o.translations.map(t => (t.marketLanguageId === langId ? { ...t, label } : t))
      return { ...o, translations }
    })
    update({ options })
  }

  const removeOption = (optIdx: number) => {
    update({ options: field.options.filter((_, i) => i !== optIdx) })
  }

  // Załączenie pliku z opcjami — bez parsowania i bez ruszania opcji pola.
  // Plik odkłada się w stanie pola; parsowanie i dołożenie nowych `value`
  // dzieje się dopiero przy zapisie całego formularza (sync).
  const attachOptionsFile = (file: File) => {
    update({ pendingOptionsFile: file })
  }

  // Eksport obecnych opcji do CSV (UTF-8 z BOM, kolumny: value + kody języków).
  // Round-trip: ten sam plik można zaimportować z powrotem.
  const handleExportCsv = () => {
    const headers = ['value', ...languages.map(lang => lang.code)]
    const escape = (val: string) => `"${(val ?? '').replace(/"/g, '""')}"`
    const lines = [headers.map(escape).join(',')]

    for (const opt of field.options) {
      const row = [
        opt.value,
        ...languages.map(lang => opt.translations.find(t => t.marketLanguageId === lang.id)?.label ?? ''),
      ]
      lines.push(row.map(escape).join(','))
    }

    const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `options-field-${field.id ?? 'new'}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  // Lista zachowuje oryginalne indeksy (handlery operują na indeksie w field.options),
  // dlatego filtrujemy pary [opcja, index] i dopiero tniemy do limitu renderu.
  const filteredOptions = field.options
    .map((opt, optIdx) => ({ opt, optIdx }))
    .filter(({ opt }) => {
      if (!optionFilter.trim()) return true
      const q = optionFilter.trim().toLowerCase()
      if (opt.value.toLowerCase().includes(q)) return true
      return opt.translations.some(t => t.label.toLowerCase().includes(q))
    })
  const visibleOptions = filteredOptions.slice(0, OPTIONS_RENDER_CAP)

  const firstLabel = field.translations[0]?.label

  return (
    <Card variant="outlined" sx={{ mb: 2, opacity: field.isActive ? 1 : 0.5 }}>
      <CardContent>
        {/* Header row */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={FIELD_TYPE_LABELS[field.type]} size="small" color="primary" variant="outlined" />
            <Select
              value={field.formSection}
              onChange={e => update({ formSection: e.target.value as FieldSection })}
              size="small"
              sx={{ minWidth: 100, fontSize: 12 }}
            >
              {(Object.entries(FIELD_SECTION_LABELS) as [FieldSection, string][]).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <Typography fontWeight={600}>{firstLabel || <em style={{ color: '#aaa' }}>No label yet</em>}</Typography>
            {field.required && <Chip label="Required" size="small" color="warning" />}
            {!field.id && <Chip label="New" size="small" color="success" />}
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={onMoveUp} disabled={index === 0} title="Move up">
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1} title="Move down">
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <FormControlLabel
              control={
                <Switch checked={field.required} onChange={e => update({ required: e.target.checked })} size="small" />
              }
              label="Required"
              sx={{ ml: 1 }}
            />
            {['single_select', 'dropdown', 'multi_select'].includes(field.type) && (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.isScored}
                    onChange={e => update({ isScored: e.target.checked })}
                    size="small"
                  />
                }
                label="Scored"
                title="Włączone = pole liczy się do oceny poprawności (isFullyCorrect). Wyłącz dla pola-selekcji bez poprawnej odpowiedzi (np. duży dropdown wyboru produktu)."
                sx={{ ml: 1 }}
              />
            )}
            <FormControlLabel
              control={
                <Switch checked={field.isActive} onChange={e => update({ isActive: e.target.checked })} size="small" />
              }
              label="Active"
            />
            <IconButton size="small" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton size="small" color="error" onClick={onDelete}>
              <TrashIcon />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box mt={2}>
            {/* Translations */}
            <Typography variant="subtitle2" mb={1}>
              Translations
            </Typography>
            <Tabs value={langTab} onChange={(_, v) => setLangTab(v)} sx={{ mb: 2 }}>
              {languages.map((lang, i) => (
                <Tab key={lang.id} label={lang.nativeName || lang.name} value={i} />
              ))}
            </Tabs>
            {languages.map((_, i) => (
              <Box key={i} display={langTab === i ? 'block' : 'none'}>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <TextField
                    label="Label"
                    size="small"
                    fullWidth
                    value={field.translations[i]?.label ?? ''}
                    onChange={e => setTranslation(i, { label: e.target.value })}
                  />
                  <TextField
                    label="Placeholder"
                    size="small"
                    fullWidth
                    value={field.translations[i]?.placeholder ?? ''}
                    onChange={e => setTranslation(i, { placeholder: e.target.value })}
                  />
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={field.translations[i]?.description ?? ''}
                    onChange={e => setTranslation(i, { description: e.target.value })}
                  />
                </Box>
              </Box>
            ))}

            {/* Restriction */}
            {['short_text', 'long_text', 'file_text'].includes(field.type) && (
              <Box mt={3}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" mb={1}>
                  Restriction
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <Select
                    value={field.restrictionType ?? 'characters'}
                    onChange={e => update({ restrictionType: e.target.value as 'characters' | 'words' })}
                    size="small"
                    disabled={field.type === 'short_text'}
                    sx={{ minWidth: 130 }}
                  >
                    <MenuItem value="characters">Characters</MenuItem>
                    <MenuItem value="words">Words</MenuItem>
                  </Select>
                  <TextField
                    type="number"
                    size="small"
                    label="Limit"
                    value={field.restrictionValue ?? ''}
                    onChange={e =>
                      update({ restrictionValue: e.target.value ? parseInt(e.target.value, 10) : undefined })
                    }
                    disabled={field.type === 'short_text'}
                    inputProps={{ min: 1, max: 10000 }}
                    sx={{ width: 110 }}
                  />
                </Box>
              </Box>
            )}

            {/* Options */}
            {HAS_OPTIONS.includes(field.type) && (
              <Box mt={3}>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle2">Options ({field.options.length})</Typography>
                  <Box display="flex" gap={1}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.csv"
                      hidden
                      onChange={e => {
                        const file = e.target.files?.[0]
                        if (file) attachOptionsFile(file)
                        e.target.value = '' // pozwól wybrać ten sam plik ponownie
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadFileIcon />}
                      disabled={!marketId}
                      onClick={() => fileInputRef.current?.click()}
                      title="Załącz plik Excel/CSV (kolumny: value + kody języków, np. en, vi). Zostanie wczytany przy zapisie formularza — dołoży tylko nowe value."
                    >
                      Attach file
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      disabled={field.options.length === 0}
                      onClick={handleExportCsv}
                      title="Pobierz obecne opcje jako CSV (ten sam format można zaimportować z powrotem)."
                    >
                      Export
                    </Button>
                  </Box>
                </Box>

                {/* Załączony plik czeka na zapis formularza — nic jeszcze nie
                    zmieniło się w opcjach. */}
                {field.pendingOptionsFile && (
                  <Box mb={2} p={1.5} border="1px solid" borderColor="info.main" borderRadius={1} bgcolor="info.50">
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        icon={<UploadFileIcon />}
                        label={field.pendingOptionsFile.name}
                        size="small"
                        onDelete={() => update({ pendingOptionsFile: undefined })}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {'Zostanie wczytany przy zapisie formularza — dołoży tylko opcje z nowym „value”.'}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {field.options.length > OPTIONS_RENDER_CAP && (
                  <TextField
                    label="Filter options"
                    size="small"
                    fullWidth
                    value={optionFilter}
                    onChange={e => setOptionFilter(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}

                {visibleOptions.map(({ opt, optIdx }) => (
                  <Box key={opt.id ?? optIdx} mb={2} p={1.5} border="1px solid" borderColor="divider" borderRadius={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip label={opt.value} size="small" />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={opt.isCorrect}
                            onChange={e => updateOption(optIdx, { isCorrect: e.target.checked })}
                            size="small"
                          />
                        }
                        label="Correct"
                      />
                      <IconButton size="small" color="error" onClick={() => removeOption(optIdx)}>
                        <TrashIcon />
                      </IconButton>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {languages.map(lang => (
                        <TextField
                          key={lang.id}
                          label={`Label (${lang.nativeName || lang.name})`}
                          size="small"
                          value={opt.translations.find(t => t.marketLanguageId === lang.id)?.label ?? ''}
                          onChange={e => setOptionTranslation(optIdx, lang.id, e.target.value)}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}

                {filteredOptions.length > visibleOptions.length && (
                  <Typography variant="caption" color="text.secondary">
                    Showing {visibleOptions.length} of {filteredOptions.length} — refine the filter to see more.
                  </Typography>
                )}
                {field.options.length > 0 && filteredOptions.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No options match the filter.
                  </Typography>
                )}

                <Box display="flex" gap={1} mt={1}>
                  <TextField
                    label="New option value (internal key)"
                    size="small"
                    value={newOptionValue}
                    onChange={e => setNewOptionValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addOption()}
                  />
                  <Button variant="outlined" startIcon={<Add />} onClick={addOption}>
                    Add
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

// ── FormBuilder ───────────────────────────────────────────────────────────────

type FormBuilderProps = {
  marketId?: string
  fields: LocalField[]
  setFields: Dispatch<SetStateAction<LocalField[]>>
  languages: MarketLanguage[]
  isLoading: boolean
  newFieldType: FieldType
  setNewFieldType: (type: FieldType) => void
  addField: () => void
  deleteField: (index: number) => void
  moveField: (index: number, direction: 'up' | 'down') => void
  onSaveVersionSnapshot: () => void
}

const FormBuilder = ({
  marketId,
  fields,
  setFields,
  languages,
  isLoading,
  newFieldType,
  setNewFieldType,
  addField,
  deleteField,
  moveField,
  onSaveVersionSnapshot,
}: FormBuilderProps): JSX.Element => {
  if (isLoading) return <RequestStatus data={undefined} isLoading />
  if (!Array.isArray(languages)) return <RequestStatus data={{ error: undefined }} isLoading={false} />

  return (
    <>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        <Button variant="outlined" color="secondary" onClick={onSaveVersionSnapshot}>
          Save version snapshot
        </Button>
      </Box>

      {/* Add field row */}
      <Box mb={3} display="flex" gap={2} alignItems="center">
        <Select
          value={newFieldType}
          onChange={e => setNewFieldType(e.target.value as FieldType)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          {(Object.entries(FIELD_TYPE_LABELS) as [FieldType, string][]).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
        <Button variant="outlined" startIcon={<Add />} onClick={addField}>
          Add field
        </Button>
      </Box>

      {/* Field list */}
      {fields.length === 0 && <Typography color="text.secondary">No fields yet. Add the first field above.</Typography>}

      {fields.map((field, index) => (
        <FieldCard
          key={field.id ?? `new-${index}`}
          field={field}
          index={index}
          total={fields.length}
          marketId={marketId}
          languages={languages}
          onChange={updated => setFields(prev => prev.map((f, i) => (i === index ? updated : f)))}
          onDelete={() => deleteField(index)}
          onMoveUp={() => moveField(index, 'up')}
          onMoveDown={() => moveField(index, 'down')}
        />
      ))}
    </>
  )
}

export default FormBuilder
